/* ═══════════════════════════════════════════════════════════════════
   HARMONY SOUNDS — bliss-aesthetic cues for the alignment ritual.
   Softer than Echo: emphasises deep singing-bowl bloom + hushed
   choir-like pads. Zero arcade blips. Sine-first, slow attacks,
   long releases, just-intonation intervals.

   Zero-storage, zero-cost, offline-first.

   API (unchanged so callers keep working):
     • select   — tile selected
     • swap     — two tiles exchanged
     • align    — alignment detected on the board
     • resolve  — user commits the alignment
     • shuffle  — board reshuffled
   ═══════════════════════════════════════════════════════════════════ */
import { getAudioCtx } from './audioCore';
import { makeBus, bell, swish, tap, riffle, gliss } from './luxeSfx';


const MUTE_KEY = 'harmony_sfx_muted';
let ctxRef = null;
let masterRef = null;

/* Track every currently-playing oscillator so a fresh trigger can
   silence the previous bloom before starting the next one (prevents
   overlapping align/resolve tails when the user matches several
   rows back-to-back). */
const activeVoices = [];

const readMuted = () => {
  try { return window.localStorage.getItem(MUTE_KEY) === '1'; }
  catch (_) { return false; }
};

export const setMuted = (m) => {
  try { window.localStorage.setItem(MUTE_KEY, m ? '1' : '0'); }
  catch (_) { /* noop */ }
};

const getCtx = () => {
  if (readMuted()) return null;
  if (!ctxRef || !masterRef) {
    try {
      ctxRef = getAudioCtx();
      if (!ctxRef) return null;
      masterRef = ctxRef.createGain();
      masterRef.gain.value = 0.42;
      // Soft global low-pass — keeps the palette warm and bell-like.
      const lp = ctxRef.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 4800;
      lp.Q.value = 0.6;
      masterRef.connect(lp);
      lp.connect(ctxRef.destination);
    } catch (_) { return null; }
  }
  if (ctxRef.state !== 'running') {
    try { ctxRef.resume(); } catch (_) { /* noop */ }
  }
  return ctxRef;
};

const bus = (ctx) => makeBus(ctx, masterRef);
const track = (g, osc) => {
  const entry = { osc, g };
  activeVoices.push(entry);
  osc.onended = () => { const i = activeVoices.indexOf(entry); if (i >= 0) activeVoices.splice(i, 1); };
};

/* Ramp every active voice to silence and stop it. Called by long-tail
   triggers (align / resolve) before starting a new one so the previous
   bloom does not overlap the new one. */
export const stopActive = () => {
  if (!ctxRef) return;
  const t = ctxRef.currentTime;
  activeVoices.slice().forEach(({ osc, g }) => {
    try {
      g.gain.cancelScheduledValues(t);
      const cur = Math.max(g.gain.value || 0.0001, 0.0001);
      g.gain.setValueAtTime(cur, t);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
    } catch (_) { /* noop */ }
    try { osc.stop(t + 0.08); } catch (_) { /* noop */ }
  });
  activeVoices.length = 0;
};

/* ─── SELECT · soft lacquer tap with a tiny crystal glint ─── */
export const select = () => {
  const ctx = getCtx(); if (!ctx) return;
  const t0 = ctx.currentTime;
  tap(ctx, bus(ctx), t0, 0.16);
  bell(ctx, bus(ctx), 1567.98, t0 + 0.01, 0.05, 0.6);
};

/* ─── SWAP · tiles gliding past each other ─── */
export const swap = () => {
  const ctx = getCtx(); if (!ctx) return;
  const t0 = ctx.currentTime;
  swish(ctx, bus(ctx), t0, 0.2, 0.2, 700, 2600);
  tap(ctx, bus(ctx), t0 + 0.17, 0.12);
};

/* ─── ALIGN · harp arpeggio crowned by a bell ─── */
export const align = () => {
  const ctx = getCtx(); if (!ctx) return;
  stopActive();
  const t0 = ctx.currentTime;
  gliss(ctx, bus(ctx), t0, [392, 493.88, 587.33, 739.99, 783.99], 0.08, 0.13, track);
  bell(ctx, bus(ctx), 1174.66, t0 + 0.42, 0.12, 2.0, track);
};

/* ─── RESOLVE · deep warm bell chord with a crystal cascade ─── */
export const resolve = () => {
  const ctx = getCtx(); if (!ctx) return;
  stopActive();
  const t0 = ctx.currentTime;
  [130.81, 196, 261.63].forEach((f, i) => bell(ctx, bus(ctx), f, t0 + i * 0.04, 0.2 - i * 0.03, 3.2, track));
  [1046.5, 1318.51, 1567.98, 2093].forEach((f, i) => bell(ctx, bus(ctx), f, t0 + 0.5 + i * 0.22, 0.06, 1.4, track));
};

/* ─── SHUFFLE · a deck of tiles riffling ─── */
export const shuffle = () => {
  const ctx = getCtx(); if (!ctx) return;
  stopActive();
  riffle(ctx, bus(ctx), ctx.currentTime, 9, 0.05);
};
